import { tt } from "../i18n/tt";
import { lazyI18n } from "../i18n/lazyText";
// 첫 신청 코치마크(스포트라이트) — 상태 판정과 단계 정의.
//
// 홈 첫 진입에서 「작품 선택 → 작품 사용 신청하기」까지 손으로 가리키는 2단계 안내다.
// 기획·시안: 디자인 캔버스 「레이블리 레슨 안내 팝업 시안」(시안 A 채택) · 가이드라인 Google Docs
// 「레이블리 인앱 안내(코치마크) 적용 가이드라인 v0.1」.
//
// 계정 단위의 「이수」는 서버가 안다 — 첫 신청 레슨을 마치면 `work_lesson_completion` 이 생기고
// `get_my_bonus_opportunities` 의 튜토리얼 항목이 pending 에서 빠진다. 여기서는 그 항목이
// pending 인 동안만 자동 시작하고, 「건너뛰기」만 기기 로컬에 기억한다(재진입은 등급 가점 카드의
// 「작품 둘러보기」).

export const FIRST_APPLY_GUIDE_KEY = "laeebly.guide.first_apply";
export const GUIDE_QUERY_PARAM = "guide"; // ?guide=first_apply — 조건과 무관하게 강제 시작(QA·디자인 검토용)
export const GUIDE_ANCHOR_FIRST_WORK = "home.first-work";
export const GUIDE_ANCHOR_APPLY_BUTTON = "work.apply-button";

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

/** 'skipped' | 'done' | null */
export function readGuideState(storage = safeStorage()) {
  try {
    const v = storage?.getItem(FIRST_APPLY_GUIDE_KEY);
    return v === "skipped" || v === "done" ? v : null;
  } catch {
    return null;
  }
}

export function markGuide(state, storage = safeStorage()) {
  try {
    if (state) storage?.setItem(FIRST_APPLY_GUIDE_KEY, state);
    else storage?.removeItem(FIRST_APPLY_GUIDE_KEY);
  } catch {
    /* 저장 불가는 무시 — 안내는 다음 진입에 한 번 더 뜰 뿐이다 */
  }
}

/** 서버 가점 항목 중 첫 신청 레슨(튜토리얼)이 아직 남아 있나 */
export function hasPendingTutorial(items) {
  return Array.isArray(items) && items.some((it) => it && it.kind === "tutorial" && it.status === "pending");
}

export function isGuideForced(search) {
  try {
    return new URLSearchParams(search || "").get(GUIDE_QUERY_PARAM) === "first_apply";
  } catch {
    return false;
  }
}

/**
 * 자동 시작 조건 — 강제 파라미터 > (작품 1개 이상 AND 튜토리얼 pending AND 건너뛴 적 없음).
 * 화면을 막는 안내라 조건이 하나라도 빠지면 띄우지 않는다.
 */
export function shouldAutoStartFirstApplyGuide({ items, hasWorks, forced = false, storage = safeStorage() }) {
  if (forced) return true;
  // 2026-09-07 최종안(기획 docs/ops/work-apply-cancel-plan.md §12-6 1): 안내는 안내로 끝난다.
  // 코치마크 「다음」 → 레슨 「다음」 → 접수로 이어지던 사슬이 "원치 않는 작품이 신청됐다"는 CS 의 원인이었다.
  // 자동 시작은 끄고, ?guide=first_apply 강제(QA·디자인 검토)와 헤더 「화면 안내 다시 보기」만 남긴다.
  // 첫 신청 안내는 홈 시작 도움말 카드(글·링크)와 가점 카드 → 레슨 직접 열기가 맡는다.
  void items; void hasWorks; void storage;
  return false;
}

export const GUIDE_ANCHOR_CHANNEL_SHEET = "channel.pick-sheet";

// 단계별 카피 — 버튼 라벨은 화면의 실제 라벨과 글자 그대로 맞춘다(「작품 사용 신청하기」).
//
// 홈의 규칙(2026-09-02 시안 3): 채널을 고르지 않은 상태에서 작품을 누르면 상세 대신 **채널 선택 시트가 먼저** 뜬다.
// 그래서 채널 미선택이면 3단계(작품 → 채널 → 사용 신청), 선택돼 있으면 2단계다.
// `returned` — 시트나 상세를 열었다가 신청 없이 돌아온 상태. 같은 가림막을 또 덮으면 제자리처럼 느껴지므로
// 링과 짧은 문구만 남기고 화면은 막지 않는다(2026-09-06 대표 확인).
// 🔒 문구는 전부 getter 다(2026-09-10 버그 수정). 모듈 로드 시점에 tt() 를 굳히면 첫 화면 언어(기기 언어, 예: 영어 크롬)로 고정되고,
// 로그인 뒤 계정 언어(한국어)가 적용돼도 코치마크만 영어로 남았다. getter 는 읽는 순간의 언어를 쓴다.
const STEP_WORK_FIRST = {
  id: "work",
  anchor: GUIDE_ANCHOR_FIRST_WORK,
  get title() { return tt("a.29f153be", "먼저 마음에 드는 작품을 골라보세요"); },
  get body() { return tt("a.3da3fb16", "작품을 누르면 상세 화면이 열려요. 거기서 작품 사용 신청하기를 누르면 첫 신청 레슨이 시작됩니다."); },
  get strong() { return tt("a.578bffb8", "작품 사용 신청하기"); },
  placement: "right",
  blocking: true,
};
const STEP_WORK_FIRST_NEEDS_CHANNEL = {
  // 스프레드(...STEP_WORK_FIRST)는 getter 값을 그 순간 굳혀 버린다 — 언어 지연 문제가 되살아나므로 필드를 직접 잇는다
  id: STEP_WORK_FIRST.id,
  anchor: STEP_WORK_FIRST.anchor,
  placement: STEP_WORK_FIRST.placement,
  blocking: STEP_WORK_FIRST.blocking,
  get title() { return STEP_WORK_FIRST.title; },
  get strong() { return STEP_WORK_FIRST.strong; },
  get body() { return tt("a.b1a860c6", "작품을 누르면 먼저 신청할 채널을 고르고, 이어서 상세 화면이 열려요. 거기서 작품 사용 신청하기를 누르면 첫 신청 레슨이 시작됩니다."); },
};
const STEP_WORK_RETURNED = {
  id: "work",
  anchor: GUIDE_ANCHOR_FIRST_WORK,
  get title() { return tt("a.fc732fff", "다시 작품을 골라 주세요"); },
  get body() { return tt("a.36c77df5", "작품 상세에서 작품 사용 신청하기를 누르면 첫 신청 레슨이 시작돼요. 아무 작품이나 골라도 됩니다."); },
  get strong() { return tt("a.578bffb8", "작품 사용 신청하기"); },
  placement: "right",
  blocking: false,
};
const STEP_CHANNEL = {
  id: "channel",
  anchor: GUIDE_ANCHOR_CHANNEL_SHEET,
  get title() { return tt("a.1f7b3ca6", "이 작품을 신청할 채널을 골라주세요"); },
  get body() { return tt("a.a1b2a7c8", "채널을 고르면 작품 상세가 열려요. 수익 배분율과 식별코드는 채널을 골라야 보입니다."); },
  get strong() { return tt("a.dadc73c8", "채널을 고르면"); },
  placement: "top",
  blocking: false,
};
const STEP_APPLY = {
  id: "apply",
  anchor: GUIDE_ANCHOR_APPLY_BUTTON,
  // 상세의 CTA 는 확인 항목(수익·가이드·식별코드·해시태그)을 다 마쳐야 열린다 — 그래서 이 단계는
  // 화면을 막지 않고(blocking=false) 버튼만 비춘다. 확인 항목은 사용자가 직접 눌러야 한다.
  get title() { return tt("a.799832a2", "확인 항목을 마치고 사용 신청을 눌러보세요"); },
  get body() { return tt("a.22c4fc05", "수익 쉐어·가이드·식별코드·해시태그를 차례로 확인하면 작품 사용 신청하기 버튼이 열려요. 누르면 첫 신청 레슨(약 3분)이 시작됩니다."); },
  get strong() { return tt("a.578bffb8", "작품 사용 신청하기"); },
  placement: "top",
  blocking: false,
};

/**
 * 흐름의 단계 목록. needsChannel 은 **시작 시점**의 채널 미선택 여부로 고정한다 — 도중에 채널을 고르면
 * 채널 단계를 건너뛰고 번호만 이어진다(STEP 1 → STEP 3). 번호가 흔들리는 것보다 낫다.
 */
export function buildFirstApplySteps({ needsChannel = false, returned = false } = {}) {
  const work = returned ? STEP_WORK_RETURNED : needsChannel ? STEP_WORK_FIRST_NEEDS_CHANNEL : STEP_WORK_FIRST;
  return needsChannel ? [work, STEP_CHANNEL, STEP_APPLY] : [work, STEP_APPLY];
}

// 이전 호환 — 2단계 기본 목록
export const FIRST_APPLY_STEPS = buildFirstApplySteps();

// ── 코치마크 밖의 안내 지점들 (가이드라인 v0.1 적용 지점 맵 P0-4 · P1 · P2) ─────────────────────

/** 홈이 「건너뛰기」를 알릴 때 쏘는 window 이벤트 — TopNavBar 가 듣고 재진입 힌트를 띄운다(P0-4) */
export const GUIDE_EVENT_SKIPPED = "laeebly:guide-skipped";
/** 메뉴의 「화면 안내 다시 보기」가 가는 주소 */
export const GUIDE_REPLAY_PATH = "/home?guide=first_apply";

/** `?guide=<id>` 값 그대로 — first_apply 외에 apply-pending · brand-channel · work-condition · admin-test-toggle 도 강제 가능 */
export function readForcedGuide(search) {
  try {
    return new URLSearchParams(search || "").get(GUIDE_QUERY_PARAM) || null;
  } catch {
    return null;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const BANNER_DISMISS_DAYS = 30;
const bannerKey = (id) => `laeebly.guide.banner.${id}`;
const seenKey = (id) => `laeebly.guide.seen.${id}`;
const sessionKey = (id) => `laeebly.guide.session.${id}`;

function safeSession() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

/** 배너 닫기 — 30일 숨김(가이드라인 4-3). 빈 상태 배너는 조건이 유지되는 한 계속 보이므로 여기 안 쓴다 */
export function isBannerDismissed(id, now = Date.now(), storage = safeStorage()) {
  try {
    const ts = Number(storage?.getItem(bannerKey(id)));
    return Number.isFinite(ts) && ts > 0 && now - ts < BANNER_DISMISS_DAYS * DAY_MS;
  } catch {
    return false;
  }
}
export function dismissBanner(id, now = Date.now(), storage = safeStorage()) {
  try { storage?.setItem(bannerKey(id), String(now)); } catch { /* 무시 */ }
}

/** 계정·기기에서 한 번만 보이는 안내(신기능 코치마크, 테스트 토글 안내) */
export function isSeenOnce(id, storage = safeStorage()) {
  try { return storage?.getItem(seenKey(id)) === "1"; } catch { return false; }
}
export function markSeenOnce(id, storage = safeStorage()) {
  try { storage?.setItem(seenKey(id), "1"); } catch { /* 무시 */ }
}

/** 세션(탭) 안에서 한 번만 — 승인 대기 바 */
export function isSessionSeen(id, storage = safeSession()) {
  try { return storage?.getItem(sessionKey(id)) === "1"; } catch { return false; }
}
export function markSessionSeen(id, storage = safeSession()) {
  try { storage?.setItem(sessionKey(id), "1"); } catch { /* 무시 */ }
}

// P1-1 승인 대기 바(시안 C) — 선택 채널에 검토 중인 신청이 1건 이상일 때 세션당 1회
export const PENDING_BAR = lazyI18n(() => ({
  id: "apply-pending",
  label: tt("a.407cf768", "신청한 작품은 이렇게 진행돼요"),
  steps: [tt("a.bc0de124", "신청 접수"), tt("a.be87e4e4", "레이블리 검토"), tt("a.b57b6d84", "승인되면 알림 · 소재 다운로드")],
  cta: tt("a.426117d5", "채널 탭에서 보기"),
}));

// P1-3 브랜드 채널 첫 전환 배너(시안 B) — 닫으면 30일
export const BRAND_BANNER_ID = "brand-channel";

// P2-1 필터·검색 0건 배너(시안 B, 빈 상태 — 항상)
export const EMPTY_RESULT_BANNER = lazyI18n(() => ({
  get title() { return tt("a.5a111a6e", "조건에 맞는 작품이 없어요"); },
  description: tt("a.e28b9cfb", "카테고리를 풀거나 모든 채널로 바꿔보세요."),
  cta: tt("a.f2475ea9", "필터 초기화"),
  secondary: tt("a.c9ebc740", "검색어 지우기"),
}));

// P1-2 채널 작업 공간 · 신청 이력 0건 빈 상태 배너(시안 B, 항상)
export const EMPTY_APPLICATIONS_BANNER = lazyI18n(() => ({
  get title() { return tt("a.4de883e4", "아직 신청한 작품이 없어요"); },
  description: tt("a.262faa09", "작품을 골라 사용 신청하면 여기에 신청 이력이 쌓여요."),
  steps: [tt("a.b0cb971c", "작품 선택"), tt("a.a54136ce", "사용 신청 누르기"), tt("a.df6e3b34", "첫 신청 레슨 · 동의")],
  cta: tt("a.598edfef", "작품 둘러보기"),
}));

// P2-2 신기능 코치마크(1단계) — 출시 후 30일 안 첫 방문 1회. 데스크톱 필터 패널이 보일 때만.
// 순서 = 우선순위: 위에서부터 「아직 안 봤고 · 기간 안이고 · when(ctx) 가 참」인 첫 항목 하나만 고른다(세션에 코치마크는 하나).
// `when` — 선택. 그 사람에게 의미 있는 안내만(루키 필터는 루키에게만, 멤버십 구독료 예고는 원장 카드가 그려질 때만). 없으면 전원.
// 2026-09-07 등급제 배포 안내(docs/ops/tier-rollout-communication-plan.md §6 「신기능 코치마크 3개」): 루키 필터 › 가점 카드 › 멤버십 구독료 예고.
// 채널 한도 코치마크는 제외(같은 문서 — 2026-09-22 부터 기존 회원도 10/1 적용이지만 채널 화면의 안내 상자가 대신한다). 「작품 표시」(구 작품 조건)는 뒤쪽.
export const FEATURE_GUIDES = lazyI18n(() => [
  {
    id: "rookie-work-filter",
    anchor: "filter.rookie-work",
    // 2026-09-27 작품 표시(시안 B) — 자동 선택은 2026-09-06 부터 꺼져 있어 「켜져 있어요」가 틀린 말이었다. 누르는 법으로 바꿨다
    get title() { return tt("a.mark.rookieGuideTitle", "「루키 작품」만 골라 볼 수 있어요"); },
    get body() { return tt("a.mark.rookieGuideBody", "「루키 작품」 표시를 누르면 루키 5편에 들어가는 작품만 보여요. 한 번 더 누르면 풀려요."); },
    get strong() { return tt("a.mark.rookieGuideStrong", "「루키 작품」"); },
    placement: "right",
    releasedAt: "2026-09-07",
    days: 30,
    when: (ctx) => Boolean(ctx && ctx.isRookieViewer),
  },
  {
    id: "bonus-card",
    anchor: "home.bonus-card",
    get title() { return tt("a.380fcf93", "새로 생긴 등급 가점"); },
    get body() { return tt("a.b3d339d8", "공지 확인·레슨으로 이달 가점을 모아요. 매월 1일 등급 결정에 더해져요."); },
    get strong() { return tt("a.1e58aece", "이달 가점"); },
    placement: "bottom",
    releasedAt: "2026-09-07",
    days: 30,
    when: (ctx) => Boolean(ctx && ctx.hasBonusCard),
  },
  {
    id: "fee-forecast",
    anchor: "home.fee-forecast",
    get title() { return tt("a.cf94dbcd", "11월부터의 멤버십 구독료 예고"); },
    get body() { return tt("a.089f23f0", "지금은 0원 · 유예 중이에요. 11월 1일부터 그달 확정 등급 기준으로 청구돼요."); },
    get strong() { return tt("a.227031fb", "지금은 0원"); },
    placement: "bottom",
    releasedAt: "2026-09-07",
    days: 30,
    when: (ctx) => Boolean(ctx && ctx.hasFeeForecast),
  },
  {
    // 2026-09-27 「작품 조건」 라디오 → 「작품 표시」 칩(시안 B). id·기간은 그대로(문구만) — 블록 안 설명 한 줄이 새 방식을 알려 준다
    id: "work-condition",
    anchor: "filter.work-condition",
    get title() { return tt("a.mark.guideTitle", "「작품 표시」로 골라 보세요"); },
    get body() { return tt("a.mark.guideBody", "카드에 붙은 표시를 누르면 그 표시가 붙은 작품만 보여요. 여러 개를 함께 고를 수 있어요."); },
    get strong() { return tt("a.mark.guideStrong", "「작품 표시」"); },
    placement: "right",
    releasedAt: "2026-09-04",
    days: 30,
  },
  {
    // 2026-10-28 대표 결정 — 카드 조건이 아이콘 6칸으로 바뀐 것을 처음 한 번 알린다(캔버스 4차 시안 ④). 휴대폰에서도 띄운다(`mobile`)
    id: "condition-icons",
    anchor: "home.condition-icons",
    get title() { return tt("a.cicon.guideTitle", "조건이 아이콘으로 바뀌었어요"); },
    get body() { return tt("a.cicon.guideBody", "색이 있는 아이콘이 이 작품에 해당하는 조건이에요. 아이콘 줄을 누르면 뜻과 할 일을 볼 수 있어요."); },
    get strong() { return tt("a.cicon.guideStrong", "아이콘 줄을 누르면"); },
    placement: "bottom",
    releasedAt: "2026-10-28",
    days: 30,
    mobile: true,
  },
  {
    id: "guide-new-docs",
    anchor: "nav.menu",
    title: tt("a.1feb6db8", "가이드에 새 문서가 생겼어요"),
    body: tt("a.2ca0b51f", "온보딩 순서 · 프로모션·번들 · 가이드라인 읽는 법 · 저작권 대응 · 계약과 해지 · 앱 사용하기. 화면 곳곳의 ⓘ 도움말도 같이 생겼어요."),
    strong: tt("a.fa3ef760", "새 문서"),
    placement: "bottom",
    releasedAt: "2026-09-21",
    days: 30,
  },
]);
/**
 * 지금 띄울 신기능 안내 하나 — 강제 id(?guide=<id>) 는 기간·1회·when 을 모두 무시한다(QA용 · 대상이 없으면 코치마크가 fail-open).
 * ctx: { isRookieViewer, hasBonusCard, hasFeeForecast } — 홈이 넘긴다. 없으면 when 이 있는 항목은 전부 건너뛴다.
 */
export function pickFeatureGuide({ now = new Date(), storage = safeStorage(), forced = null, ctx = {} } = {}) {
  if (forced) {
    const f = FEATURE_GUIDES.find((g) => g.id === forced);
    if (f) return f;
  }
  const t = now.getTime();
  return FEATURE_GUIDES.find((g) => {
    if (typeof g.when === "function") {
      let ok = false;
      try { ok = Boolean(g.when(ctx || {})); } catch { ok = false; }
      if (!ok) return false;
    }
    if (isSeenOnce(g.id, storage)) return false;
    const start = new Date(`${g.releasedAt}T00:00:00+09:00`).getTime();
    return t >= start && t < start + g.days * DAY_MS;
  }) || null;
}

// P1-4 라이브 → 테스트 토글 첫 조작(어드민) — 1단계, 1회. 토글은 새로고침을 일으키므로 pending 키로 넘긴다
export const TEST_TOGGLE_GUIDE = {
  id: "admin-test-toggle",
  anchor: "admin.test-toggle",
  get title() { return tt("a.fb9a293e", "테스트 모드로 바꿨어요"); },
  get body() { return tt("a.e42cc27e", "정산·번들 화면이 테스트 데이터를 보고 저장해요. 라이브 데이터는 바뀌지 않아요. 다시 끄면 라이브로 돌아갑니다."); },
  get strong() { return tt("a.9c9a0e96", "테스트 데이터"); },
  placement: "bottom",
};
export const PENDING_GUIDE_KEY = "laeebly.guide.pending";
export function setPendingGuide(id, storage = safeStorage()) {
  try { storage?.setItem(PENDING_GUIDE_KEY, id); } catch { /* 무시 */ }
}
export function takePendingGuide(storage = safeStorage()) {
  try {
    const v = storage?.getItem(PENDING_GUIDE_KEY) || null;
    if (v) storage?.removeItem(PENDING_GUIDE_KEY);
    return v;
  } catch {
    return null;
  }
}
